import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { fetchMcpProStatus } from "util/mcpApi";
import { MCP_SALES_EMAIL } from "util/mcpSetup";
import ClaudeLogo from "./ClaudeLogo";
import styles from "./claudeAnnouncement.module.scss";

// Bump the suffix to show a new announcement to everyone again.
const DISMISSED_KEY_PREFIX = "pivony_claude_announcement_dismissed";
const SHOWN_AT_KEY_PREFIX = "pivony_claude_announcement_shown_at";
export const MCP_SETTINGS_PATH = "/settings/integrations?view=mcp";
const LEARN_MORE_URL = {
  en: "https://pivony.com/integrations/mcp",
  tr: "https://pivony.com/tr/entegrasyonlar/mcp",
};
const SALES_URL = `mailto:${MCP_SALES_EMAIL}?subject=${encodeURIComponent("Pivony MCP")}`;
// Let the console finish its first paint before the popup appears.
const SHOW_DELAY_MS = 1500;
// After "Later" (or closing it) the popup comes back after this long.
export const SHOW_AGAIN_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

export const dismissedKey = (uid) => `${DISMISSED_KEY_PREFIX}:${uid}`;
export const shownAtKey = (uid) => `${SHOWN_AT_KEY_PREFIX}:${uid}`;

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    // No storage (private mode): never nag on every page load.
    return "1";
  }
};

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (_) {
    /* ignore */
  }
};

/** Whether the popup may show now: not dismissed and not shown in the last week. */
export const isDue = (uid, now = Date.now()) => {
  if (read(dismissedKey(uid))) return false;
  const shownAt = Number(read(shownAtKey(uid)) || 0);
  return !shownAt || now - shownAt >= SHOW_AGAIN_AFTER_MS;
};

export const learnMoreUrl = (language) =>
  String(language || "").toLowerCase().startsWith("tr") ? LEARN_MORE_URL.tr : LEARN_MORE_URL.en;

/**
 * "Pivony for Claude" announcement on console pages, for every user.
 *
 * - Shown at most once a week (Emre, 2026-11-04); "Later" (or closing it) brings it back a week later.
 * - "Don't show again" and "Connect to Claude" stop it for good (per user, per browser).
 * - Organizations with Pivony MCP get "Connect to Claude"; the others get "Talk to sales" and
 *   "Learn more" (pivony.com), since the setup page only works with Pivony MCP.
 *
 * The matching What's New entry is created in the What's New CMS.
 */
const ClaudeAnnouncement = ({ uid, enabled }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [entitled, setEntitled] = useState(false);

  useEffect(() => {
    if (!enabled || !uid) return undefined;
    if (!isDue(uid)) return undefined;
    let cancelled = false;
    let timer;
    fetchMcpProStatus().then((res) => {
      // Without an answer we cannot pick the right button; try again on the next page load.
      if (cancelled || !res || res.error || res.cancel) return;
      timer = setTimeout(() => {
        write(shownAtKey(uid), String(Date.now()));
        setEntitled(!!res.entitled);
        setOpen(true);
      }, SHOW_DELAY_MS);
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, uid]);

  const dismissForGood = () => {
    write(dismissedKey(uid), "1");
    setOpen(false);
  };

  const connect = () => {
    dismissForGood();
    history.push(MCP_SETTINGS_PATH);
  };

  return (
    <Modal
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
      width={520}
      centered
      className={styles.modal}
    >
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.logoTile}>
            <ClaudeLogo size={30} />
          </span>
          <div>
            <span className={styles.badge}>{t("claude_announcement.badge")}</span>
            <h2 className={styles.title}>{t("claude_announcement.title")}</h2>
          </div>
        </div>
        <p className={styles.lead}>{t("claude_announcement.lead")}</p>
        <div className={styles.example}>
          <span className={styles.exampleLabel}>{t("claude_announcement.example_label")}</span>
          <p className={styles.exampleText}>{t("claude_announcement.example")}</p>
        </div>
        <ul className={styles.points}>
          <li>{t("claude_announcement.point_signin")}</li>
          <li>{t("claude_announcement.point_access")}</li>
        </ul>
        {!entitled ? <p className={styles.note}>{t("claude_announcement.not_active")}</p> : null}
        <div className={styles.actions}>
          {entitled ? (
            <Button type="primary" onClick={connect}>
              {t("claude_announcement.connect")}
            </Button>
          ) : (
            <>
              <Button type="primary" href={SALES_URL}>
                {t("claude_announcement.contact_sales")}
              </Button>
              <Button href={learnMoreUrl(i18n.language)} target="_blank" rel="noopener noreferrer">
                {t("claude_announcement.learn_more")}
              </Button>
            </>
          )}
          <Button type="text" className={styles.later} onClick={() => setOpen(false)}>
            {t("claude_announcement.later")}
          </Button>
        </div>
        <button type="button" className={styles.dismiss} onClick={dismissForGood}>
          {t("claude_announcement.dont_show_again")}
        </button>
      </div>
    </Modal>
  );
};

export default ClaudeAnnouncement;
